import React, { useState } from 'react';
import { Music, Upload, Loader, CheckCircle, AlertCircle } from 'lucide-react';
import { completeAudioUpload } from '../../../services/audioManagementService';

const AudioLineTable = ({ lines, selectedLines, onSelectLine, onUploadSuccess }) => {
  const [uploadingLines, setUploadingLines] = useState(new Set());
  const [uploadErrors, setUploadErrors] = useState({});
  const [uploadSuccess, setUploadSuccess] = useState({});

  const truncateText = (text, maxLength = 80) => {
    if (!text || text?.length <= maxLength) return text;
    return text?.substring(0, maxLength) + '...';
  };

  const handleAudioUpload = async (event, lineId) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Clear previous messages
    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors?.[lineId];
      return newErrors;
    });
    setUploadSuccess(prev => {
      const newSuccess = { ...prev };
      delete newSuccess?.[lineId];
      return newSuccess;
    });

    // Set uploading state
    setUploadingLines(prev => new Set([...prev, lineId]));

    try {
      // Upload audio file
      const result = await completeAudioUpload(file, lineId);

      // Set success message
      setUploadSuccess(prev => ({
        ...prev,
        [lineId]: 'Audio uploaded successfully!'
      }));

      // Clear success message after 3 seconds
      setTimeout(() => {
        setUploadSuccess(prev => {
          const newSuccess = { ...prev };
          delete newSuccess?.[lineId];
          return newSuccess;
        });
      }, 3000);

      // Notify parent component
      if (onUploadSuccess) {
        onUploadSuccess(lineId);
      }

    } catch (error) {
      console.error('Upload error:', error);
      setUploadErrors(prev => ({
        ...prev,
        [lineId]: error?.message || 'Upload failed. Please try again.'
      }));

      // Clear error message after 5 seconds
      setTimeout(() => {
        setUploadErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors?.[lineId];
          return newErrors;
        });
      }, 5000);
    } finally {
      setUploadingLines(prev => {
        const newSet = new Set(prev);
        newSet?.delete(lineId);
        return newSet;
      });
    }

    // Reset file input
    event.target.value = '';
  };

  const renderUploadStatus = (lineId) => {
    if (uploadingLines?.has(lineId)) {
      return (
        <div className="flex items-center space-x-2 text-sm text-purple-600">
          <Loader className="w-4 h-4 animate-spin" />
          <span>Uploading...</span>
        </div>
      );
    }

    if (uploadSuccess?.[lineId]) {
      return (
        <div className="flex items-center space-x-2 text-sm text-green-600">
          <CheckCircle className="w-4 h-4" />
          <span>{uploadSuccess?.[lineId]}</span>
        </div>
      );
    }

    if (uploadErrors?.[lineId]) {
      return (
        <div className="flex items-center space-x-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4" />
          <span>{uploadErrors?.[lineId]}</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="w-12 px-6 py-3">
                <input
                  type="checkbox"
                  checked={selectedLines?.size === lines?.length && lines?.length > 0}
                  onChange={() => {}}
                  className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                />
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Line ID
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Play
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Act/Scene
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Character
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Line Text
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Upload Audio
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {lines?.map((line) => (
              <tr 
                key={line?.line_id}
                className={`hover:bg-gray-50 transition-colors ${
                  selectedLines?.has(line?.line_id) ? 'bg-purple-50' : ''
                }`}
              >
                <td className="px-6 py-4 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={selectedLines?.has(line?.line_id)}
                    onChange={() => onSelectLine(line?.line_id)}
                    className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                  />
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-gray-900">
                    {line?.line_id}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900">
                    {line?.plays?.title || 'Unknown'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-600">
                    A{line?.act} · S{line?.scene}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm font-medium text-gray-900">
                    {line?.speaker}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-600">
                    {truncateText(line?.line_text)}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col space-y-2">
                    <label
                      htmlFor={`upload-${line?.line_id}`}
                      className="inline-flex items-center justify-center space-x-2 px-3 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                    >
                      <Upload className="w-4 h-4" />
                      <span>Upload MP3</span>
                    </label>
                    <input
                      id={`upload-${line?.line_id}`}
                      type="file"
                      accept="audio/mpeg,audio/mp3,audio/wav,audio/x-m4a,audio/m4a"
                      onChange={(e) => handleAudioUpload(e, line?.line_id)}
                      disabled={uploadingLines?.has(line?.line_id)}
                      className="hidden"
                    />
                    {renderUploadStatus(line?.line_id)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Mobile Cards */}
      <div className="md:hidden divide-y divide-gray-200">
        {lines?.map((line) => (
          <div 
            key={line?.line_id}
            className={`p-4 ${selectedLines?.has(line?.line_id) ? 'bg-purple-50' : ''}`}
          >
            <div className="flex items-start space-x-3">
              <input
                type="checkbox"
                checked={selectedLines?.has(line?.line_id)}
                onChange={() => onSelectLine(line?.line_id)}
                className="mt-1 w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-semibold text-gray-900">
                    {line?.line_id}
                  </span>
                  <Music className="w-4 h-4 text-red-500" />
                </div>
                <div className="text-sm text-gray-600 mb-1">
                  <span className="font-medium">{line?.plays?.title}</span>
                  {' · '}
                  <span>Act {line?.act}, Scene {line?.scene}</span>
                </div>
                <div className="text-sm font-medium text-purple-600 mb-2">
                  {line?.speaker}
                </div>
                <p className="text-sm text-gray-600 line-clamp-2 mb-3">
                  {line?.line_text}
                </p>
                
                {/* Mobile Upload Button */}
                <div className="flex flex-col space-y-2">
                  <label
                    htmlFor={`upload-mobile-${line?.line_id}`}
                    className="inline-flex items-center justify-center space-x-2 px-3 py-2 border border-purple-600 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                  >
                    <Upload className="w-4 h-4" />
                    <span>Upload MP3</span>
                  </label>
                  <input
                    id={`upload-mobile-${line?.line_id}`}
                    type="file"
                    accept="audio/mpeg,audio/mp3,audio/wav,audio/x-m4a,audio/m4a"
                    onChange={(e) => handleAudioUpload(e, line?.line_id)}
                    disabled={uploadingLines?.has(line?.line_id)}
                    className="hidden"
                  />
                  {renderUploadStatus(line?.line_id)}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AudioLineTable;